import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { Kpi } from '@/types/dashboards';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 1 });

function formatValue(value: Kpi['value'], unit: Kpi['unit']): string {
  if (value === null) return '—';
  if (typeof value === 'string') return value;
  return unit ? `${NUM.format(value)} ${unit}` : NUM.format(value);
}

function trustBandClass(score: number): string {
  if (score >= 80) return 'border-transparent bg-emerald-100 text-emerald-800';
  if (score >= 50) return 'border-transparent bg-amber-100 text-amber-800';
  return 'border-transparent bg-red-100 text-red-800';
}

export interface FleetHealthTilesProps {
  kpis: Kpi[] | undefined;
  isLoading: boolean;
  error: Error | null;
  emptyMessage?: string;
}

/**
 * Fleet Health panel — `GET /api/v1/dashboards/fleet-health`. Spec §13's
 * six-tile data-trust grid: missing baselines, missing location, midnight-
 * timestamp ingest bug, missing make/model/year, baseline freshness, and a
 * composite `trust_score`. The `trust_score` tile gets a colour band badge
 * (high/moderate/low, matching the endpoint's own `trust_band` thresholds
 * of 80 / 50) — every other tile renders as a plain story card.
 */
export function FleetHealthTiles({
  kpis,
  isLoading,
  error,
  emptyMessage = 'No fleet-health data available yet.',
}: FleetHealthTilesProps): JSX.Element {
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-3 text-sm text-red-800">
          Could not load fleet health: {error.message}
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="space-y-2 p-4">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!kpis || kpis.length === 0) {
    return (
      <Card>
        <CardContent className="py-3 text-sm text-muted-foreground">{emptyMessage}</CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      {kpis.map((kpi) => {
        const isTrustScore = kpi.name === 'trust_score' && typeof kpi.value === 'number';
        return (
          <Card key={kpi.name} className={cn(isTrustScore && 'border-2')}>
            <CardContent className="space-y-1.5 p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {kpi.name}
                </div>
                {isTrustScore && (
                  <Badge className={trustBandClass(kpi.value as number)}>
                    {(kpi.value as number) >= 80
                      ? 'high'
                      : (kpi.value as number) >= 50
                        ? 'moderate'
                        : 'low'}
                  </Badge>
                )}
              </div>
              <div className="text-2xl font-semibold tabular-nums">
                {formatValue(kpi.value, kpi.unit)}
              </div>
              <p className="text-sm text-muted-foreground">{kpi.story}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default FleetHealthTiles;
